import { formatDisplayTime } from "../lib/display-time";
import {
  REPORT_COLUMN_LABELS,
  REPORT_LIFECYCLE_LABELS,
  REPORT_METHOD_LABELS,
  type ReportColumnKey,
  type ReportScanLifecycleState,
  type ReportScanMethod,
  type ReportScanSort,
} from "./report-scan-filters";
import { checkpointDisplayLabel, gpsQualityLabel, type ScanRow } from "./scan-list";

// Scan-report CSV export (pure helpers; Pilot-0 "export route reports as CSV" criterion).
//
// Built over the SAME redacted supervisor_scan_list rows the Live Activity surface reads — the
// CSV carries labels and the view's redacted guard ref ONLY. It NEVER includes raw tokens,
// hashes, UUIDs/ids, device ids, or GPS coordinates (GPS appears only as the quality label,
// e.g. "±8m" / "GPS denied"). RFC 4180: header row first, CRLF record separators, and any
// field containing a comma, double-quote, CR, or LF is double-quoted with inner quotes doubled.

export const SCAN_REPORT_COLUMNS = [
  "Captured (device)",
  "Received (server)",
  "Checkpoint",
  "Site",
  "Route",
  "Guard",
  "Method",
  "Status",
  "GPS quality",
  "Validation notes",
] as const;

const COLUMN_KEY_TO_HEADER: Record<ReportColumnKey, (typeof SCAN_REPORT_COLUMNS)[number]> = {
  captured: "Captured (device)",
  received: "Received (server)",
  checkpoint: "Checkpoint",
  site: "Site",
  route: "Route",
  guard: "Guard",
  method: "Method",
  status: "Status",
  gps: "GPS quality",
  validation_notes: "Validation notes",
};

const UTF8_BOM = "\uFEFF";

export interface ScanReportMetadata {
  readonly windowLabel?: string;
  readonly site: string;
  readonly sites?: string;
  readonly guard: string;
  readonly route?: string;
  readonly checkpoint?: string;
  readonly method?: string;
  readonly presence?: string;
  readonly status?: string;
  readonly reviewRequired?: string;
  readonly dates: string;
  readonly sort?: string;
  readonly columns?: string;
  readonly capped: boolean;
  readonly exportedAt: Date;
}

/**
 * Neutralize spreadsheet formula injection: management-entered names (checkpoints/sites/routes)
 * are free text, and Excel/Sheets execute cells starting with = + - @ (or tab). Prefixing a
 * single quote renders them inert text.
 */
function neutralizeFormula(value: string): string {
  return /^\s*[=+\-@]/.test(value) ? `'${value}` : value;
}

/** Quote a field per RFC 4180 only when it contains a comma, double-quote, CR, or LF. */
function escapeCsvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Final export safety net: redact secret-shaped text if a safe view ever contains one. */
function redactSecretLikeText(value: string): string {
  return value
    .replace(/chk_[A-Za-z0-9]{20,}/g, "[redacted]")
    .replace(/\beyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+\b/g, "[redacted]")
    .replace(/\bBearer\s+[A-Za-z0-9._-]+/gi, "[redacted]")
    .replace(/\bsb_secret_[A-Za-z0-9_-]+/gi, "[redacted]")
    .replace(/\bservice_role\b/gi, "[redacted]")
    .replace(/\b[a-f0-9]{64}\b/gi, "[redacted]")
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, "[redacted]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted]");
}

/** One serializer for the header, scan records, and the trailing metadata block. */
function serializeCsvRow(fields: readonly string[]): string {
  return fields
    .map(redactSecretLikeText)
    .map(neutralizeFormula)
    .map(escapeCsvField)
    .join(",");
}

export { serializeCsvRow as serializeReportCsvRow };

function cellForColumn(row: ScanRow, column: ReportColumnKey, timeZone: string): string {
  switch (column) {
    case "captured":
      return formatDisplayTime(row.deviceTime, timeZone);
    case "received":
      return formatDisplayTime(row.serverReceivedAt, timeZone);
    case "checkpoint":
      return checkpointDisplayLabel(row);
    case "site":
      return row.siteName ?? "";
    case "route":
      return row.routeName ?? "";
    case "guard":
      return row.guardName ?? "";
    case "method":
      return row.scanMethod;
    case "status":
      return row.lifecycleState;
    case "gps":
      return gpsQualityLabel(row);
    case "validation_notes":
      return row.validationNotes ?? "";
  }
}

export function reportSortMetadataLabel(sort: ReportScanSort): string {
  return `${REPORT_COLUMN_LABELS[sort.column]} (${sort.direction === "asc" ? "ascending" : "descending"})`;
}

export function reportColumnsMetadataLabel(columns: readonly ReportColumnKey[]): string {
  return columns.map((key) => REPORT_COLUMN_LABELS[key]).join(", ");
}

export function reportMethodsMetadataLabel(methods: readonly ReportScanMethod[]): string {
  if (methods.length === 0) return "All methods";
  return methods.map((method) => REPORT_METHOD_LABELS[method]).join(", ");
}

export function reportStatusesMetadataLabel(
  states: readonly ReportScanLifecycleState[],
): string {
  if (states.length === 0) return "All statuses";
  return states.map((state) => REPORT_LIFECYCLE_LABELS[state]).join(", ");
}

/** Build the full CSV (header + one record per scan). Times render via formatDisplayTime. */
export function buildScanReportCsv(
  rows: readonly ScanRow[],
  opts: {
    readonly timeZone: string;
    readonly metadata: ScanReportMetadata;
    readonly columns?: readonly ReportColumnKey[];
  },
): string {
  const columns: readonly ReportColumnKey[] =
    opts.columns !== undefined && opts.columns.length > 0
      ? opts.columns
      : [
          "captured",
          "received",
          "checkpoint",
          "site",
          "route",
          "guard",
          "method",
          "status",
          "gps",
          "validation_notes",
        ];
  const header = columns.map((key) => COLUMN_KEY_TO_HEADER[key]);
  const lines: string[] = [serializeCsvRow(header)];
  for (const row of rows) {
    lines.push(
      serializeCsvRow(columns.map((column) => cellForColumn(row, column, opts.timeZone))),
    );
  }
  lines.push("");
  lines.push(serializeCsvRow(["# SecOps scan report · metadata"]));
  lines.push(serializeCsvRow(["Sites", opts.metadata.sites ?? opts.metadata.site]));
  lines.push(serializeCsvRow(["Guard", opts.metadata.guard]));
  lines.push(serializeCsvRow(["Route", opts.metadata.route ?? "All routes"]));
  lines.push(serializeCsvRow(["Checkpoint", opts.metadata.checkpoint ?? "Any on route"]));
  lines.push(serializeCsvRow(["Method", opts.metadata.method ?? "All methods"]));
  lines.push(serializeCsvRow(["Presence", opts.metadata.presence ?? "All"]));
  lines.push(serializeCsvRow(["Status", opts.metadata.status ?? "All statuses"]));
  lines.push(
    serializeCsvRow(["Review required", opts.metadata.reviewRequired ?? "No"]),
  );
  lines.push(serializeCsvRow(["Dates", opts.metadata.dates]));
  lines.push(serializeCsvRow(["Window", opts.metadata.windowLabel ?? `Most recent ${rows.length} matching scans`]));
  lines.push(serializeCsvRow(["Capped", opts.metadata.capped ? "Yes" : "No"]));
  lines.push(serializeCsvRow(["Sort", opts.metadata.sort ?? "Captured (device) (descending)"]));
  lines.push(
    serializeCsvRow([
      "Columns",
      opts.metadata.columns ?? columns.map((key) => REPORT_COLUMN_LABELS[key]).join(", "),
    ]),
  );
  lines.push(serializeCsvRow(["Time zone", opts.timeZone]));
  lines.push(
    serializeCsvRow([
      "Exported at",
      formatDisplayTime(opts.metadata.exportedAt.toISOString(), opts.timeZone),
    ]),
  );
  return lines.join("\r\n");
}

/** "secops-scan-report-YYYY-MM-DD.csv" — the calendar date in the account's DISPLAY timezone
 *  (matching every timestamp inside the file), not UTC. */
export function reportFileName(now: Date, timeZone: string): string {
  // en-CA renders YYYY-MM-DD; Intl handles the zone conversion.
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return `secops-scan-report-${date}.csv`;
}

/** UTF-8 BOM keeps the existing Excel-on-Windows behavior for both browser and native export. */
export function createScanReportBlob(csv: string): Blob {
  return new Blob([UTF8_BOM, csv], { type: "text/csv;charset=utf-8" });
}

/**
 * Hand the CSV to the browser as a file download: Blob → object URL → temporary anchor click,
 * then revoke. `doc` is injectable so tests can pass a stub (jsdom lacks createObjectURL;
 * tests stub URL.createObjectURL/revokeObjectURL).
 */
export function triggerCsvDownload(csv: string, fileName: string, doc: Document = document): void {
  const blob = createScanReportBlob(csv);
  const url = URL.createObjectURL(blob);
  const anchor = doc.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  doc.body.appendChild(anchor);
  anchor.click();
  doc.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
